import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { EnterValueDialogComponent } from '../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../dialogs/enter-value-dialog/enter-value-response';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../services/message-service/message.service';
import { GruppeTier, GruppeTierBemerkung } from '../classes/gruppe-tier';
import { GruppeTierService } from '../gruppe-tier.service';

export interface GruppeTierBemerkungSetzenDialogData {
    tiere: GruppeTier[];
}

@Component({
    selector: 'kiene-gruppe-tier-bemerkung-setzen-dialog',
    templateUrl: './gruppe-tier-bemerkung-setzen-dialog.component.html',
    styleUrls: ['./gruppe-tier-bemerkung-setzen-dialog.component.scss'],
})
export class GruppeTierBemerkungSetzenDialogComponent implements OnInit, OnDestroy {
    isSaving = false;
    isCreating = false;
    isLoading = true;
    bemerkungen: string[] = [];
    selected = new Set<string>();

    private loadSub: Subscription;

    constructor(
        private readonly dialogRef: MatDialogRef<GruppeTierBemerkungSetzenDialogComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: GruppeTierBemerkungSetzenDialogData,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly dialog: MatDialog,
        private readonly api: KieneBackendApiService,
        private readonly gruppeTierService: GruppeTierService,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.loadBemerkungen();
    }

    ngOnDestroy(): void {
        this.loadSub?.unsubscribe();
    }

    removeAll(): void {
        this.selected.clear();
    }

    save(): void {
        if (!this.data?.tiere?.length) {
            this.messageService.errorMessage('Keine Tiere ausgewählt!', null);
            return;
        }

        const texts = Array.from(this.selected);
        if (texts.length === 0) {
            this.dialogRef.close(false);
            return;
        }

        this.isSaving = true;
        const requests = texts.map(text => this.postBemerkung(text));
        forkJoin(requests).subscribe({
            next: () => {
                this.isSaving = false;
                this.messageService.infoMessage('Bemerkungen für alle ausgewählten Tiere aktualisiert.');
                this.dialogRef.close(true);
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Bemerkungen konnten nicht aktualisiert werden!', err);
            },
        });
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    openCreateDialog(): void {
        if (this.isCreating) {
            return;
        }

        const input = new EnterValueInput();
        input.title = 'Bemerkung anlegen';
        input.addInputValue('Bezeichnung', 'text');

        this.dialog
            .open(EnterValueDialogComponent, { data: input })
            .afterClosed()
            .subscribe({
                next: (result: EnterValueResponse) => {
                    const text = result?.getValue(0)?.trim?.();
                    if (!result?.result || !text) {
                        return;
                    }
                    if (!this.bemerkungen.includes(text)) {
                        this.bemerkungen = [text, ...this.bemerkungen];
                    }
                    this.selected.add(text);
                },
            });
    }

    isChecked(text: string): boolean {
        return this.selected.has(text);
    }

    onSelectionChange(event: MatSelectionListChange): void {
        if (!event.options?.length) {
            return;
        }

        for (const option of event.options) {
            const text = String(option.value ?? '').trim();
            if (!text) {
                continue;
            }
            if (option.selected) {
                this.selected.add(text);
            } else {
                this.selected.delete(text);
            }
        }
    }

    private loadBemerkungen(): void {
        this.isLoading = true;
        this.loadSub?.unsubscribe();
        this.loadSub = this.gruppeTierService.loadLetzteBemerkungen().subscribe({
            next: texte => {
                this.bemerkungen = texte;
                this.isLoading = false;
            },
            error: err => {
                this.isLoading = false;
                this.messageService.errorMessage('Bemerkungen konnten nicht geladen werden!', err);
            },
        });
    }

    private postBemerkung(text: string): Observable<unknown> {
        const gtm = new GruppeTierBemerkung();
        gtm.gt_ids = (this.data?.tiere ?? []).map(tier => tier.gt_id).filter(id => id > 0);
        gtm.bemerkung = text;
        return this.api.post(this.apiBaseUrl + 'gruppe_tier_bemerkung/update_kundenportal_bemerkungen.php', gtm);
    }
}
